import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CgRename } from 'react-icons/cg'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../../ui-kit'
import useToast from '../../../hooks/useToast'
import type {
  MediaLibrarySearchCollectionResponse,
  MediaLibraryCollectionUpdateErrorResponse,
  MediaLibraryCollectionUpdateSuccessResponse,
} from '../../../interfaces'
import { MLContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import { mediaCollectionNameRegex } from '../utils'

interface Props {
  isOpen: boolean
  onClose(): void
}

interface FormProps {
  newCollectionName: string
}

const RenameMediaCollectionModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [selectedCollection, setSelectedCollection] =
    useState<MediaLibrarySearchCollectionResponse>()
  const [formError, setFormError] = useState<string>('')
  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const [
    {
      selectedMediaCollections,
      selectedBreadcrumbMediaCollection,
      refetchMediaCollections,
      refetchViewableParent,
    },
    dispatch,
  ] = useContext(MLContext)

  const nameRegularExpression = /^[a-zA-Z0-9_-]+$/u

  useEffect(() => {
    setSelectedCollection(selectedMediaCollections[0])
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          enableReinitialize
          initialValues={{
            newCollectionName: selectedCollection?.name ?? '',
          }}
          onSubmit={async (values): Promise<void> => {
            setFormError('')
            const res = (await request.patch(
              `/api/v2/media-collections/${selectedCollection?.mediaCollectionId}`,
              {
                name: values.newCollectionName,
              },
            )) as
              | MediaLibraryCollectionUpdateErrorResponse
              | MediaLibraryCollectionUpdateSuccessResponse

            if (request.error) {
              if ([400, 404, 401].includes(response.status)) {
                setFormError(
                  (res as MediaLibraryCollectionUpdateErrorResponse).message,
                )
              } else {
                setFormError(
                  t(
                    'error_in_renaming_media_collection',
                    'Error in renaming media-collection, please try again after sometime',
                  ),
                )
              }
            } else {
              toast({
                description: t(
                  'media_collection_renamed_successfullt',
                  'Collection renamed successfully',
                ),
                status: 'success',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })

              window.trackEvent({
                action: 'Submit',
                category: 'Media collection',
                label: 'Rename',
                value: 1,
              })

              setFormError('')
              if (
                selectedBreadcrumbMediaCollection?.mediaCollectionId ===
                selectedCollection?.mediaCollectionId
              ) {
                await refetchViewableParent()
              }
              await new Promise(resolve => setTimeout(resolve, 1500))
              onClose()
              await refetchMediaCollections()
            }
          }}
          validationSchema={Yup.object().shape({
            newCollectionName: Yup.string()
              .required('Required')
              .matches(mediaCollectionNameRegex, 'Name cannot have a slash.'),
          })}
        >
          {({ isSubmitting, submitForm }) => (
            <>
              <ModalHeader>
                <Icon
                  as={CgRename}
                  color="appBattleshipGrey"
                  backgroundColor="appPaleGrey"
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="full"
                  marginRight="0.5em"
                />
                {t('rename_media_collection', 'Rename collection')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {formError ? (
                  <Alert status="error" mt="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <VStack spacing={4} alignItems="flex-start">
                  <Text color="appBattleshipGrey" maxWidth="100%">
                    {t(
                      'rename_media_collection_description',
                      'The collection name can only have alphanumeric characters, underscores, and hyphens, and should start with an alphanumeric.',
                    )}
                  </Text>
                  <Field name="newCollectionName">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        marginTop="1em"
                        isInvalid={Boolean(
                          form.errors.newCollectionName &&
                            form.touched.newCollectionName,
                        )}
                        isRequired
                      >
                        <FormLabel>
                          {t(
                            'new_media_collection_name',
                            'New Collection Name',
                          )}
                        </FormLabel>
                        <Input
                          {...field}
                          placeholder={selectedCollection?.name}
                        />
                        {form.errors.newCollectionName &&
                        form.touched.newCollectionName ? (
                          <FormErrorMessage mt="0.25em">
                            {form.errors.newCollectionName}
                          </FormErrorMessage>
                        ) : (
                          <Box height="1.55em" />
                        )}
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  onClick={async () => {
                    await submitForm()
                    dispatch({
                      type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
                    })
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default RenameMediaCollectionModal
